import { useGetBenefitVerificationTrendQuery } from "store/queries/benefitComparison";

import { accuracyPercent, trendCaption } from "./benefitComparisonWording";

const RUNS = 30;

/**
 * CLE-2820 follow-up — THE TREND: engine accuracy, one point per NIGHTLY run.
 *
 * 🚨 ONE POINT PER RUN, NOT PER RE-SCORE. `BenefitVerificationSnapshot` is written only by the
 * nightly job; `POST /benefitComparison/rescore` deliberately writes none, so pressing "Re-run
 * engine on these checks" four times in an afternoon cannot draw four points and read as four days
 * of movement.
 *
 * 🚨 NOT FILTERED BY ACCOUNT, PAYER OR BENEFIT. A snapshot is the whole verified set as it stood
 * that night; filtering rows that carry no account would silently answer a different question. The
 * caption says what the line is over.
 *
 * Before the first run it is a dash with no explanatory copy — the dash fills in on its own.
 */
export function BenefitWorklistTrend() {
  const { data } = useGetBenefitVerificationTrendQuery({ runs: RUNS });
  const trend = data?.data;
  const points = (trend?.points ?? [])
    .map((point) => ({
      computedAt: point.computedAt,
      percent: accuracyPercent(point.matches, point.verified),
    }))
    .filter(
      (point): point is { computedAt: string; percent: number } =>
        point.percent !== null
    );

  if (points.length < 2) {
    return (
      <span
        className="flex-1 flex align-items-center justify-content-center text-4xl font-bold text-color-secondary"
        data-testid="benefitWorklist.trend.empty"
      >
        —
      </span>
    );
  }

  const values = points.map((point) => point.percent);
  const low = Math.min(...values);
  const high = Math.max(...values);
  // A flat line still needs a band, or every point lands on the same pixel row.
  const span = high - low < 0.5 ? 1 : high - low;
  const x = (index: number) => (300 * index) / (points.length - 1);
  const y = (value: number) => 88 - (80 * (value - low)) / span;

  return (
    <>
      <svg
        width="100%"
        height="64"
        viewBox="0 0 300 96"
        preserveAspectRatio="none"
        role="img"
        aria-label={`Engine accuracy over the last ${
          points.length
        } nightly runs, ${low.toFixed(1)} to ${high.toFixed(1)} percent`}
        data-testid="benefitWorklist.trend.line"
      >
        <polyline
          fill="none"
          stroke="#1d4ed8"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
          points={points
            .map((point, index) => `${x(index)},${y(point.percent)}`)
            .join(" ")}
        />
        <circle
          cx={x(points.length - 1)}
          cy={y(values[values.length - 1])}
          r="3"
          fill="#1d4ed8"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <span
        className="align-self-center text-color-secondary"
        data-testid="benefitWorklist.trend.caption"
      >
        {trendCaption(values[0], values[values.length - 1], points.length)}
      </span>
    </>
  );
}
